import ColumnsSelector from "./components/ColumnsSelector";
import DuplicateNameDialog from "./components/DuplicateNameDialog";
import JoinsPanel from "./components/JoinsPanel";
import { PageLoader } from "./components/LoadingOverlay";
import PreAggFilterPopover from "./components/PreAggFilterPopover";
import ReportPreviewDialog from "./components/ReportPreviewDialog";
import SaveBlockedMessage from "./components/SaveBlockedMessage";
import TableSelector from "./components/TableSelector";
import { useDuplications } from "./hooks/useDuplications";
import { downloadJson, sanitizeReportFilename } from "./hooks/useExportReport";
import { usePreAggFilters } from "./hooks/usePreAggFilters";
import { useReportExportContext } from "./hooks/useReportExportContext";
import { useSaveReport } from "./hooks/useSaveReport";
import { useSortAlpha } from "./hooks/useSortAlpha";
import { REPORT_CONFIGURATION_NAME } from "./types/constants";
import { getErrorMessage, isFetchBaseQueryError } from "./types/types";
import { moveTabConfiguration } from "./utils/configurationUtils";
import { DEFAULT_DATE_FORMAT } from "./utils/dateUtils";
import { renameDuplicateSource } from "./utils/duplicationUtils";
import { extractVarColumns } from "./utils/expressionEditorUtils";
import { deriveFilterMaps } from "./utils/filterSpecUtils";
import { pruneJoinConfigs } from "./utils/joinUtils";
import {
  isSpecValid,
  isReportNameKnown,
  pruneConfiguration,
  qualifySharedDisplayNames,
  saveDisabledReason,
  reconcileSelectionWithAvailableColumns,
  applyOutputColumnChange,
  deriveSelectedReports,
} from "./utils/reportBuilderUtils";
import { SpecBuilder } from "./utils/specBuilderSupport";
import { Save as SaveIcon, Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import Button from "components/common/Button";
import { getReportSourcePrefix } from "config";
import * as labels from "config.labels";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import * as reportsV3Service from "services/reportsV3Service";
import { useConfigQuery } from "services/staticDataService";
import * as staticDataHooks from "store/hooks/staticDataHooks";

// A stable empty list: a fresh [] each render re-runs the dialog's summary.
const NO_COLUMNS = [];

const ReportBuilder = ({ embedded = false, onSaveSuccess }) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const navigate = useNavigate();
  const location = useLocation();
  const { reportId: urlReportId } = useParams();
  const reportId = urlReportId === "new" ? undefined : urlReportId;
  const isEditing = !!reportId;

  const {
    data: tables,
    isLoading: tablesLoading,
  } = reportsV3Service.useGetTablesQuery(undefined);
  const {
    data: existingReport,
    isLoading: reportLoading,
  } = reportsV3Service.useGetReportQuery(reportId, {
    skip: !reportId,
  });

  const { data: savedReports } = reportsV3Service.useGetSavedReportsQuery();
  const selectableSavedReports = useMemo(
    () => (savedReports || []).filter((r) => !r.multiOutputReport),
    [savedReports]
  );
  const [sortAlpha] = useSortAlpha();
  const {
    duplications,
    pendingDuplication,
    beginDuplication,
    beginEditDuplication,
    cancelDuplication,
    completeDuplication,
    isDuplicateNameAvailable,
    findDuplication,
    fetchDuplicationColumns,
    localReports,
    setDuplicationsFromReport,
  } = useDuplications({
    tables: tables ?? [],
    savedReports: savedReports ?? [],
  });

  const locationState = location.state;
  const initialName = locationState?.reportName || "";
  const initialDescription = locationState?.reportDescription || "";

  const [reportName, setReportName] = useState(initialName);
  const [reportDescription, setReportDescription] = useState(
    initialDescription
  );
  const [selectedTables, setSelectedTables] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [outputColumns, setOutputColumns] = useState([]);
  const selectedColumns = useMemo(() => outputColumns.map((c) => c.column), [
    outputColumns,
  ]);
  const [groupSpec, setGroupSpec] = useState(null);
  const [joinConfigs, setJoinConfigs] = useState([]);
  const [error, setError] = useState(null);

  const [reportColumnsMap, setReportColumnsMap] = useState({});

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTableName, setPreviewTableName] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewRequestRef = useRef(0);

  const [
    fetchColumnsForTable,
  ] = reportsV3Service.useLazyGetColumnsForTablesQuery();
  const [
    fetchColumnsForReport,
  ] = reportsV3Service.useLazyGetColumnsForReportQuery();
  const [executeReport] = reportsV3Service.useExecuteReportMutation();
  const [runImmediateReport] = reportsV3Service.useRunImmediateReportMutation();
  const [
    fetchConfiguration,
  ] = reportsV3Service.useLazyGetReportConfigurationQuery();
  const [
    storeConfiguration,
  ] = reportsV3Service.useStoreReportConfigurationMutation();

  const isNameKnown = useMemo(
    () => isReportNameKnown(reportName, savedReports),
    [reportName, savedReports]
  );

  const {
    data: availableColumnsData,
    isLoading: columnsLoading,
    isFetching: columnsFetching,
  } = reportsV3Service.useGetColumnsForTablesQuery(selectedTables, {
    skip: selectedTables.length === 0,
  });

  const availableColumns = columnsFetching ? undefined : availableColumnsData;

  const allSelectedSources = useMemo(
    () => [...selectedTables, ...selectedReports.map((r) => `REPORT:${r}`)],
    [selectedTables, selectedReports]
  );

  const isMultiTable = allSelectedSources.length > 1;

  useEffect(() => {
    const sourceSet = new Set(allSelectedSources);
    setOutputColumns((prev) => {
      const next = prev.filter(
        (col) => !col.sourceTable || sourceSet.has(col.sourceTable)
      );
      return next.length === prev.length ? prev : next;
    });
    setJoinConfigs((prev) => pruneJoinConfigs(prev, sourceSet));
  }, [allSelectedSources]);

  const sourceDisplayNameMap = useMemo(() => {
    const map = {};
    (tables || []).forEach((t) => {
      map[t.internalName] = t.displayName || t.internalName;
    });
    (savedReports || []).forEach((r) => {
      map[`REPORT:${r.internalName}`] = r.displayName || r.internalName;
    });
    duplications.forEach((d) => {
      map[`${getReportSourcePrefix()}${d.internalName}`] = d.displayName;
    });
    return map;
  }, [tables, savedReports, duplications]);

  const allAvailableColumns = useMemo(() => {
    const tableCols = availableColumns || [];
    const reportCols = Object.entries(
      reportColumnsMap
    ).flatMap(([reportId, cols]) =>
      cols.map((col) => ({ ...col, sourceTable: `REPORT:${reportId}` }))
    );
    return [...tableCols, ...reportCols];
  }, [availableColumns, reportColumnsMap]);

  // Reconcile the current selection against the columns that are actually
  // available. A source that is itself a report can stop returning a column
  // (e.g. a grouped report's summary-row controls), in which case any
  // selection that depends on it must be cleaned up.
  useEffect(() => {
    // Don't prune while sources are still loading their columns, otherwise we
    // would wrongly drop selections that are merely in-flight.
    if (allSelectedSources.length === 0) return;
    if (selectedTables.length > 0 && !availableColumns) return;
    if (selectedReports.some((id) => !reportColumnsMap[id])) return;

    const availableColumnNames = new Set(
      allAvailableColumns.map((c) => c.internalName)
    );

    const {
      outputColumns: nextOutputColumns,
      groupSpec: nextGroupSpec,
    } = reconcileSelectionWithAvailableColumns({
      outputColumns,
      groupSpec,
      availableColumnNames,
    });

    if (nextOutputColumns !== outputColumns) {
      setOutputColumns(nextOutputColumns);
    }
    if (nextGroupSpec !== groupSpec) {
      setGroupSpec(nextGroupSpec);
    }
  }, [
    allAvailableColumns,
    availableColumns,
    reportColumnsMap,
    selectedReports,
    selectedTables,
    allSelectedSources,
    outputColumns,
    groupSpec,
  ]);

  const { data: dateFormatConfig } = useConfigQuery("date_format");
  const dateFormat = dateFormatConfig?.date_format ?? DEFAULT_DATE_FORMAT;

  const availableColumnsMap = useMemo(() => {
    const map = new Map();
    allAvailableColumns.forEach((col) => map.set(col.internalName, col));
    return map;
  }, [allAvailableColumns]);

  const getColDef = useCallback(
    (colName) => availableColumnsMap.get(colName) ?? null,
    [availableColumnsMap]
  );

  // The summary must embed any selected duplication, which the back-end
  // cannot resolve by name. wrapReportIfNeeded comes from useSaveReport, which
  // is called further down because it needs buildSpec, so it is reached
  // through a ref rather than by reordering the hooks.
  const wrapReportIfNeededRef = useRef(null);
  const wrapSummaryReport = useCallback(
    (report) =>
      selectedReports.length > 0 && wrapReportIfNeededRef.current
        ? wrapReportIfNeededRef.current(report)
        : report,
    [selectedReports]
  );

  const {
    preAggFilterCols,
    openFilterPanel,
    buildPreAggFilters,
    setFilterMaps,
    panelProps,
  } = usePreAggFilters({
    sourceTables: allSelectedSources,
    joinConfigs,
    outputColumns,
    getColDef,
    runImmediateReport,
    wrapReport: wrapSummaryReport,
    dateFormat,
  });

  const sourceColumnQueries = useMemo(
    () =>
      allSelectedSources.map((sourceId) => ({
        data: allAvailableColumns.filter((col) => col.sourceTable === sourceId),
      })),
    [allSelectedSources, allAvailableColumns]
  );

  useEffect(() => {
    const current = new Set(selectedReports);

    setReportColumnsMap((prev) => {
      const pruned = Object.fromEntries(
        Object.entries(prev).filter(([id]) => current.has(id))
      );
      return Object.keys(pruned).length === Object.keys(prev).length
        ? prev
        : pruned;
    });

    // this may leave fetches running after an unmount
    // however for this use case that is fine.
    selectedReports.forEach(async (reportId) => {
      try {
        const duplication = findDuplication(reportId);
        const columns = duplication
          ? await fetchDuplicationColumns(duplication)
          : await fetchColumnsForReport(reportId, true).unwrap();
        setReportColumnsMap((prev) =>
          prev[reportId] === columns ? prev : { ...prev, [reportId]: columns }
        );
      } catch {
        setReportColumnsMap((prev) => ({ ...prev, [reportId]: [] }));
      }
    });
  }, [
    selectedReports,
    fetchColumnsForReport,
    findDuplication,
    fetchDuplicationColumns,
  ]);

  useEffect(() => {
    if (existingReport) {
      setReportName(existingReport.displayName || "");
      setReportDescription(existingReport.description || "");

      let spec = existingReport.specification;

      if (existingReport.type === "MULTI_TABLE_REPORT") {
        const subReports = existingReport.reports ?? [];
        spec = subReports.find((r) => !r.notForOutput)?.specification ?? null;
        setDuplicationsFromReport(existingReport);
        setSelectedReports(deriveSelectedReports(existingReport));
      }

      if (spec) {
        const builderSourceTables = (spec.sourceTables ?? []).filter(
          (t) => !t.startsWith("REPORT:")
        );

        setSelectedTables(builderSourceTables);

        const sourceColumns = spec.sourceColumns || [];

        const sourceTableMap = {};
        const originalColumnMap = {};
        const sourceColumnsDisplayMap = {};
        sourceColumns.forEach((sc) => {
          if (sc.sourceTable) sourceTableMap[sc.column] = sc.sourceTable;
          if (sc.sourceColumn && sc.sourceColumn !== sc.column) {
            originalColumnMap[sc.column] = sc.sourceColumn;
          }
          if (sc.displayName != null)
            sourceColumnsDisplayMap[sc.column] = sc.displayName;
        });
        const aggregateMap = Object.fromEntries(
          (spec.aggregate ?? []).map((a) => [a.column, a])
        );
        const extraGroupAggMap = Object.fromEntries(
          (spec.group?.[0]?.aggregate ?? [])
            .filter(
              (a) =>
                a.expression?.sourceColumn &&
                a.expression.sourceColumn !== a.column
            )
            .map((a) => [a.column, a])
        );
        const editableOutputColumns = (spec.outputColumns || []).map((oc) => {
          const groupAggEntry = extraGroupAggMap[oc.column];
          if (groupAggEntry) {
            return {
              column: oc.column,
              displayName:
                sourceColumnsDisplayMap[oc.column] ??
                oc.displayName ??
                oc.column,
              dataType: "groupAggregate",
              func: groupAggEntry.expression.func,
              sourceColumn: groupAggEntry.expression.sourceColumn,
            };
          }
          const resolvedColumn = originalColumnMap[oc.column] || oc.column;
          const aggEntry =
            aggregateMap[resolvedColumn] ?? aggregateMap[oc.column];
          return {
            ...oc,
            displayName: sourceColumnsDisplayMap[oc.column] ?? oc.displayName,
            column: resolvedColumn,
            sourceTable: aggEntry
              ? undefined
              : oc.sourceTable || sourceTableMap[oc.column],
            ...(aggEntry
              ? {
                  dataType: "aggregate",
                  expression: aggEntry.expression,
                  requiredColumns: extractVarColumns(aggEntry.expression ?? {}),
                }
              : {}),
          };
        });
        setOutputColumns(editableOutputColumns);
        setGroupSpec(spec.group ?? null);
        setJoinConfigs(spec.joins || []);

        setFilterMaps(deriveFilterMaps(spec.preAggregationFilters));
      }
    }
  }, [existingReport, setFilterMaps, setDuplicationsFromReport]);

  const handleFormChange = useCallback((setter) => {
    return (value) => {
      setter(value);
    };
  }, []);

  const handlePreviewTable = useCallback(
    async (tableInternalName, tableDisplayName) => {
      const requestId = ++previewRequestRef.current;
      setPreviewTableName(tableDisplayName);
      setPreviewData(null);
      setPreviewError(null);
      setPreviewLoading(true);
      setPreviewOpen(true);
      try {
        const columnsResult = await fetchColumnsForTable([
          tableInternalName,
        ]).unwrap();
        if (requestId !== previewRequestRef.current) return;
        const sourceColumns = columnsResult.map((col) => ({
          sourceTable: tableInternalName,
          sourceColumn: col.internalName,
          column: col.internalName,
          displayName: col.displayName,
        }));
        const outputColumns = columnsResult.map((col) => ({
          column: col.internalName,
          displayName: col.displayName,
        }));
        const report = {
          type: "SINGLE_TABLE_REPORT",
          displayName: tableDisplayName,
          description: "",
          specification: {
            sourceTables: [tableInternalName],
            sourceColumns,
            outputColumns,
            filters: [],
            order: [],
            joins: null,
            aggregate: null,
            group: null,
          },
        };
        const data = await runImmediateReport({
          report,
          outputType: "JSON",
          limitRows: 20,
        }).unwrap();
        if (requestId !== previewRequestRef.current) return;
        setPreviewData(data);
      } catch (e) {
        if (requestId !== previewRequestRef.current) return;
        setPreviewError(e?.data || e?.message || String(e));
      } finally {
        if (requestId === previewRequestRef.current) {
          setPreviewLoading(false);
        }
      }
    },
    [fetchColumnsForTable, runImmediateReport]
  );

  const handlePreviewReport = useCallback(
    async (reportInternalName, reportDisplayName) => {
      const requestId = ++previewRequestRef.current;
      setPreviewTableName(reportDisplayName);
      setPreviewData(null);
      setPreviewError(null);
      setPreviewLoading(true);
      setPreviewOpen(true);
      try {
        const data = await executeReport({
          reportId: reportInternalName,
          outputType: "JSON",
          limitRows: 20,
        }).unwrap();
        if (requestId !== previewRequestRef.current) return;
        setPreviewData(data);
      } catch (e) {
        if (requestId !== previewRequestRef.current) return;
        setPreviewError(e?.data || e?.message || String(e));
      } finally {
        if (requestId === previewRequestRef.current) {
          setPreviewLoading(false);
        }
      }
    },
    [executeReport]
  );

  // A duplication is previewed by running its own specification, so the
  // filters chosen when it was named apply. A duplicate of a report needs its
  // parent embedded, which wrapReportIfNeeded does.
  const handlePreviewDuplication = useCallback(
    async (entry) => {
      const requestId = ++previewRequestRef.current;
      setPreviewTableName(entry.displayName);
      setPreviewData(null);
      setPreviewError(null);
      setPreviewLoading(true);
      setPreviewOpen(true);
      try {
        const report = await wrapReportIfNeededRef.current(entry);
        const data = await runImmediateReport({
          report,
          outputType: "JSON",
          limitRows: 20,
        }).unwrap();
        if (requestId !== previewRequestRef.current) return;
        setPreviewData(data);
      } catch (e) {
        if (requestId !== previewRequestRef.current) return;
        setPreviewError(e?.data || e?.message || String(e));
      } finally {
        if (requestId === previewRequestRef.current) {
          setPreviewLoading(false);
        }
      }
    },
    [runImmediateReport]
  );

  const reportDuplicationError = useCallback((e) => {
    setError(getErrorMessage(e));
  }, []);

  const handleBeginDuplication = useCallback(
    (parentSource) =>
      beginDuplication(parentSource).catch(reportDuplicationError),
    [beginDuplication, reportDuplicationError]
  );

  const handleEditDuplication = useCallback(
    (entry) => beginEditDuplication(entry).catch(reportDuplicationError),
    [beginEditDuplication, reportDuplicationError]
  );

  // Renaming a duplicate changes the source id every selection holds, so the
  // rename is carried through rather than leaving the old id to be pruned.
  const handleSaveDuplication = useCallback(
    async (name, preAggregationFilters) => {
      let outcome;
      try {
        outcome = await completeDuplication(name, preAggregationFilters);
      } catch (e) {
        // The dialog stays open, because completeDuplication only clears the
        // pending duplication once it has succeeded.
        reportDuplicationError(e);
        return;
      }
      const previous = outcome?.previousInternalName;
      if (!previous || previous === outcome.entry.internalName) return;
      const renamed = renameDuplicateSource(
        previous,
        outcome.entry.internalName,
        { selectedReports, outputColumns, joinConfigs }
      );
      setSelectedReports(renamed.selectedReports);
      setOutputColumns(renamed.outputColumns);
      setJoinConfigs(renamed.joinConfigs);
    },
    [
      completeDuplication,
      reportDuplicationError,
      selectedReports,
      outputColumns,
      joinConfigs,
    ]
  );

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false);
  }, []);

  const handleDeleteExtraColumn = useCallback((colName) => {
    setGroupSpec((prev) => {
      if (!prev?.[0]) return prev;
      return [
        {
          ...prev[0],
          aggregate: prev[0].aggregate.filter((a) => a.column !== colName),
        },
      ];
    });
    setOutputColumns((prev) => prev.filter((c) => c.column !== colName));
  }, []);

  const handleOutputChange = useCallback(
    (newColumns) => {
      const {
        outputColumns: nextOutputColumns,
        groupSpec: nextGroupSpec,
      } = applyOutputColumnChange({
        outputColumns,
        groupSpec,
        newColumns,
        availableColumns,
      });
      if (nextGroupSpec !== groupSpec) {
        setGroupSpec(nextGroupSpec);
      }
      handleFormChange(setOutputColumns)(nextOutputColumns);
    },
    [outputColumns, groupSpec, availableColumns, handleFormChange]
  );
  const buildSpec = useCallback(() => {
    const existingSpec =
      existingReport?.type === "MULTI_TABLE_REPORT"
        ? (existingReport.reports ?? []).find((r) => !r.notForOutput)
            ?.specification ?? null
        : existingReport?.specification ?? null;
    const builder = new SpecBuilder(existingSpec);
    builder.setSourceTables(allSelectedSources);
    builder.setJoins(joinConfigs);
    builder.setGrouping(groupSpec);
    builder.setTypedColumns(
      qualifySharedDisplayNames(outputColumns, sourceDisplayNameMap)
    );
    builder.setPreAggregationFilters(buildPreAggFilters());
    return builder.build();
  }, [
    allSelectedSources,
    outputColumns,
    joinConfigs,
    existingReport,
    groupSpec,
    buildPreAggFilters,
    sourceDisplayNameMap,
  ]);

  const specValid = useMemo(
    () =>
      isSpecValid(reportName, allSelectedSources, selectedColumns, joinConfigs),
    [reportName, allSelectedSources, selectedColumns, joinConfigs]
  );

  const isReadOnly = useMemo(() => {
    if (!reportId) return false;
    const match = savedReports?.find((r) => r.internalName === reportId);
    if (match) return match.canWrite === false;
    return existingReport?.canWrite === false;
  }, [reportId, savedReports, existingReport]);
  const canSave = specValid && isEditing && !isReadOnly;
  const canCreate = specValid && !isNameKnown;

  // The builder's own name does not count as taken while editing: only
  // Create is blocked by it, and Save keeps working.
  const isNameTaken =
    isNameKnown &&
    reportName.trim().toLowerCase() !==
      (existingReport?.displayName ?? "").trim().toLowerCase();

  const blockedReason = useMemo(
    () =>
      saveDisabledReason({
        selectedSources: allSelectedSources,
        selectedColumns,
        joinConfigs,
        reportName,
        isNameKnown,
      }),
    [allSelectedSources, selectedColumns, joinConfigs, reportName, isNameKnown]
  );

  const buildReportData = useCallback(
    () => ({
      type: "SINGLE_TABLE_REPORT",
      displayName: reportName.trim(),
      description: reportDescription.trim(),
      specification: buildSpec(),
    }),
    [reportName, reportDescription, buildSpec]
  );

  const handleAfterSave = useCallback(() => {
    if (onSaveSuccess) onSaveSuccess();
    else navigate(`/report-builder/${reportId}`);
  }, [onSaveSuccess, navigate, reportId]);

  const handleAfterCreate = useCallback(
    async (newReportId) => {
      if (reportId && newReportId) {
        const columnSet = new Set(outputColumns.map((col) => col.column));
        try {
          const config = await fetchConfiguration({
            reportId,
            configurationName: REPORT_CONFIGURATION_NAME,
          }).unwrap();
          if (config && Object.keys(config).length > 0) {
            // The builder makes one output tab, and the server names it after
            // the new report whether or not it wraps it in a multi-table one.
            const outputTab =
              existingReport?.reports?.find((r) => !r.notForOutput)
                ?.internalName ?? reportId;
            const pruned = moveTabConfiguration(
              pruneConfiguration(config, columnSet),
              outputTab,
              newReportId
            );
            if (Object.keys(pruned).length > 0) {
              await storeConfiguration({
                reportId: newReportId,
                configurationName: REPORT_CONFIGURATION_NAME,
                configuration: pruned,
              }).unwrap();
            }
          }
        } catch {
          // best-effort
        }
      }
      if (onSaveSuccess) onSaveSuccess();
      else if (newReportId) navigate(`/report-builder/${newReportId}`);
      else navigate("/report-builder");
    },
    [
      reportId,
      existingReport,
      outputColumns,
      fetchConfiguration,
      storeConfiguration,
      onSaveSuccess,
      navigate,
    ]
  );

  const handleSaveError = useCallback((e) => {
    if (isFetchBaseQueryError(e) && typeof e.data === "string") {
      setError(e.data);
    } else {
      setError(getErrorMessage(e));
    }
  }, []);

  const {
    handleSaveAllChanges,
    handleCreate,
    isSavingReport: isUpdating,
    isCreatingReport: isCreating,
    wrapReportIfNeeded,
  } = useSaveReport({
    savedReport: existingReport,
    buildReportData,
    onSaveSuccess: handleAfterSave,
    onCreateSuccess: handleAfterCreate,
    onError: handleSaveError,
    localReports,
  });
  wrapReportIfNeededRef.current = wrapReportIfNeeded;

  const { registerExportHandler, setIsExporting } =
    useReportExportContext() ?? {};

  useEffect(() => {
    if (!registerExportHandler) return;
    const safeName = sanitizeReportFilename(
      existingReport?.displayName || reportName.trim() || "report"
    );
    const handler = async () => {
      setIsExporting?.(true);
      try {
        const reportData = await wrapReportIfNeeded(buildReportData());
        downloadJson({ report: reportData }, `${safeName}-edited.json`);
      } finally {
        setIsExporting?.(false);
      }
    };
    return registerExportHandler(handler);
  }, [
    registerExportHandler,
    setIsExporting,
    wrapReportIfNeeded,
    buildReportData,
    existingReport,
    reportName,
  ]);

  const handleCancel = () => {
    if (onSaveSuccess) {
      onSaveSuccess();
    } else if (reportId) {
      navigate(`/report-builder/${reportId}`);
    } else {
      navigate("/report-builder");
    }
  };
  if (tablesLoading || reportLoading) {
    if (embedded) {
      return <PageLoader />;
    }
    return (
      <Component
        title={
          isEditing
            ? translateLabels(labels.REPORT_BUILDER_EDIT_REPORT, {
                default: "Edit Report",
              })
            : translateLabels(labels.REPORT_BUILDER_CREATE_REPORT, {
                default: "Create Report",
              })
        }
      >
        <PageLoader />
      </Component>
    );
  }
  const builderContent = (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <Stack
        spacing={3}
        sx={{
          flex: 1,
          overflow: "auto",
          p: 3,
          width: "100%",
        }}
      >
        <Paper sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "flex-start" }}
          >
            <TextField
              label={translateLabels(labels.REPORT_BUILDER_REPORT_NAME, {
                default: "Report Name",
              })}
              value={reportName}
              onChange={(e) => handleFormChange(setReportName)(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 250 }}
              required
              error={isNameTaken}
              helperText={
                isNameTaken
                  ? translateLabels(labels.REPORT_BUILDER_NAME_TAKEN, {
                      default: "A report with this name already exists",
                    })
                  : undefined
              }
            />
            <TextField
              label={translateLabels(labels.REPORT_BUILDER_DESCRIPTION, {
                default: "Description",
              })}
              value={reportDescription}
              onChange={(e) =>
                handleFormChange(setReportDescription)(e.target.value)
              }
              sx={{ flexGrow: 2 }}
              multiline
              maxRows={2}
            />
          </Stack>
        </Paper>
        {error && (
          <Alert severity={"error"} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 3, minWidth: 0 }}>
          <Card sx={{ width: 260, flexShrink: 1, minWidth: 0 }}>
            <CardContent>
              <Typography variant={"h6"} gutterBottom>
                {translateLabels(labels.REPORT_BUILDER_DATA_SOURCES, {
                  default: "Data Sources",
                })}
              </Typography>
              <TableSelector
                tables={tables || []}
                savedReports={selectableSavedReports}
                selectedTables={selectedTables}
                onSelectedTablesChange={setSelectedTables}
                selectedReports={selectedReports}
                onSelectedReportsChange={setSelectedReports}
                onPreviewTable={handlePreviewTable}
                onPreviewReport={handlePreviewReport}
                onPreviewDuplication={handlePreviewDuplication}
                onEditDuplication={handleEditDuplication}
                onDuplicate={handleBeginDuplication}
                duplications={duplications}
                sortAlpha={sortAlpha}
              />
            </CardContent>
          </Card>
          {allSelectedSources.length > 0 && (
            <Card sx={{ flex: 1, minWidth: 0 }}>
              <CardContent>
                <Box sx={{ display: "flex", gap: 3, minWidth: 0 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ColumnsSelector
                      key={allSelectedSources.join(",")}
                      availableColumns={allAvailableColumns}
                      outputColumns={outputColumns}
                      onOutputChange={handleOutputChange}
                      isLoading={
                        columnsLoading ||
                        selectedReports.some((id) => !reportColumnsMap[id])
                      }
                      isMultiTable={isMultiTable}
                      tableDisplayNameMap={sourceDisplayNameMap}
                      onDeleteExtraColumn={handleDeleteExtraColumn}
                      preAggFilterCols={preAggFilterCols}
                      onPreAggFilterClick={openFilterPanel}
                    />
                  </Box>
                  {isMultiTable && (
                    <Box
                      sx={{
                        width: 280,
                        flexShrink: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <JoinsPanel
                        selectedSourcesArray={allSelectedSources}
                        sourceDisplayNameMap={sourceDisplayNameMap}
                        sourceColumnQueries={sourceColumnQueries}
                        joinConfigs={joinConfigs}
                        onJoinConfigsChange={setJoinConfigs}
                      />
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </Stack>
      <Stack
        direction={"row"}
        spacing={1}
        justifyContent={"flex-end"}
        data-testid={"report-builder-actions"}
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          px: 3,
          py: 1.5,
          backgroundColor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <SaveBlockedMessage reason={blockedReason} />
        <Button onClick={handleCancel} color={"secondary"}>
          {translateLabels(labels.REPORT_BUILDER_CANCEL, {
            default: "Cancel",
          })}
        </Button>
        {isEditing && (
          <Button
            onClick={() => {
              setError(null);
              handleSaveAllChanges();
            }}
            isLoading={isUpdating}
            startIcon={<SaveIcon />}
            isDisabled={!canSave}
          >
            {translateLabels(labels.REPORT_BUILDER_SAVE_REPORT, {
              default: "Save Report",
            })}
          </Button>
        )}
        <Button
          onClick={() => {
            setError(null);
            handleCreate();
          }}
          isLoading={isCreating}
          startIcon={<AddIcon />}
          isDisabled={!canCreate}
        >
          {translateLabels(labels.REPORT_BUILDER_CREATE_REPORT, {
            default: "Create Report",
          })}
        </Button>
      </Stack>
    </Box>
  );
  return (
    <>
      {builderContent}
      <ReportPreviewDialog
        open={previewOpen}
        onClose={handleClosePreview}
        title={previewTableName}
        data={previewData}
        loading={previewLoading}
        error={previewError}
      />
      <DuplicateNameDialog
        open={pendingDuplication !== null}
        parent={pendingDuplication?.parent ?? null}
        entry={pendingDuplication?.entry ?? null}
        columns={pendingDuplication?.columns ?? NO_COLUMNS}
        isNameAvailable={isDuplicateNameAvailable}
        onCancel={cancelDuplication}
        onSave={handleSaveDuplication}
        runImmediateReport={runImmediateReport}
        wrapReport={wrapReportIfNeeded}
        dateFormat={dateFormat}
      />
      <PreAggFilterPopover panelProps={panelProps} />
    </>
  );
};
export default ReportBuilder;
